import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Paper, 
    Skeleton, 
    Chip, 
    Switch, 
    Button, 
    IconButton,
    TablePagination,
    Typography 
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { RestaurantService } from '../../../api/services/restaurantService';
import MenuCategoryModal from './MenuCategoryModal';
import EditMenuCategoryModal from './EditMenuCategoryModal';
import { extractTotalCount } from '../../../components/common/tableStyles';

export default function MenuCategoriesTab() {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Modal state
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);

    // Pagination state
    const [page, setPage] = useState(0); // MUI TablePagination is 0-indexed
    const [limit, setLimit] = useState(20);
    const [totalRecords, setTotalRecords] = useState(0);

    const fetchCategories = async () => {
        setLoading(true);
        try {
            // We pass page + 1 because our backend likely expects 1-indexed pages
            const response = await RestaurantService.getMenuCategories({ page: page + 1, limit });
            
            // Map the response data
            const dataArray = response?.data?.data || response?.data || response?.categories || [];
            setCategories(Array.isArray(dataArray) ? dataArray : []);
            setTotalRecords(extractTotalCount(response, dataArray));
        } catch (error) {
            console.error('Failed to fetch menu categories:', error);
            // Error toast is handled globally by axios interceptor
        } finally {
            setLoading(false);
        }
    };

    // Watcher: automatically fetch categories on page or limit changes
    useEffect(() => {
        fetchCategories();
    }, [page, limit]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setLimit(parseInt(event.target.value, 10));
        setPage(0); // Reset to first page
    };

    const handleEditClick = (category) => {
        setSelectedCategory(category);
        setEditModalOpen(true);
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button 
                    variant="contained" 
                    startIcon={<AddIcon sx={{ fontSize: 18 }} />} 
                    onClick={() => setCreateModalOpen(true)}
                >
                    Create New Category
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Category Name</TableCell>
                            <TableCell>Display Priority</TableCell>
                            <TableCell align="center">Visibility Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            Array.from(new Array(limit)).map((_, i) => (
                                <TableRow key={`skeleton-${i}`}>
                                    <TableCell><Skeleton width="60%" /></TableCell>
                                    <TableCell><Skeleton width="40%" /></TableCell>
                                    <TableCell align="center"><Skeleton width={60} sx={{ display: 'inline-block' }} /></TableCell>
                                    <TableCell align="right"><Skeleton width={32} sx={{ display: 'inline-block' }} /></TableCell>
                                </TableRow>
                            ))
                        ) : categories.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                                    <Typography color="text.secondary">No menu categories found.</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            categories.map((category) => (
                                <TableRow key={category.id} hover>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="bold">
                                            {category.name}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {category.displayOrder ?? 0}
                                    </TableCell>

                                    <TableCell align="center">
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                            <Typography variant="caption" color="text.secondary">
                                                {category.isActive ? 'Published' : 'Hidden'}
                                            </Typography>
                                            <Switch 
                                                checked={!!category.isActive} 
                                                size="small"
                                                color="success"
                                                disabled
                                            />
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" color="primary" onClick={() => handleEditClick(category)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                
                {/* MUI Standard Pagination Controls Footer */}
                <TablePagination
                    component="div"
                    count={categories.length === 0 ? 0 : totalRecords}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={limit}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[20, 50, 100]}
                />
            </TableContainer>

            <MenuCategoryModal 
                open={createModalOpen} 
                onClose={() => setCreateModalOpen(false)} 
                onCategoryCreated={fetchCategories} 
            />

            <EditMenuCategoryModal
                open={editModalOpen}
                onClose={() => {
                    setEditModalOpen(false);
                    setSelectedCategory(null);
                }}
                category={selectedCategory}
                onCategoryUpdated={fetchCategories}
            />
        </Box>
    );
}
